import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderDataToolbarComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderTabGroupRoutingDirective,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderThemeDirective,
  ElderToastService,
  ElderToolbarContentDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../../features/validators/id-validator'
import { CapabilitySpec } from '../../../../process/capability-spec/capabilities/capability-spec'
import { CapabilitySpecSelectDirective } from '../../../../process/capability-spec/capability-spec-select/capability-spec-select.directive'
import { CapabilitySpecService } from '../../../../process/capability-spec/capability-spec.service'
import { ConstraintSpecTableComponent } from '../../../../process/capability-spec/constraints/constraint-spec-table/constraint-spec-table.component'
import { DynamicPropertySpecBrowserComponent } from '../../../../properties/dynamic/dynamic-property-spec-browser/dynamic-property-spec-browser.component'
import { PropertyKind } from '../../../../properties/property'
import { PropertySpecTableComponent } from '../../../../properties/spec/property-spec-table/property-spec-table.component'
import { ItemSortSpecDetailComponent } from '../items/item-sort-spec-detail/item-sort-spec-detail.component'
import { WorkCenterType } from '../work-center-type'
import { WorkCenterTypeService } from '../work-center-type.service'

@Component({
  selector: 'app-work-center-type-detail',
  templateUrl: './work-center-type-detail.component.html',
  styleUrls: ['./work-center-type-detail.component.scss'],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderPaneSubtitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatTabGroup,
    ElderTabGroupRoutingDirective,
    MatTab,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    ElderTableComponent,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSelectComponent,
    CapabilitySpecSelectDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    PropertySpecTableComponent,
    DynamicPropertySpecBrowserComponent,
    ItemSortSpecDetailComponent,
    ConstraintSpecTableComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class WorkCenterTypeDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _workCenterType$: BehaviorSubject<WorkCenterType> = new BehaviorSubject(null)
  private readonly _wctCapabilities$: BehaviorSubject<CapabilitySpec[]> = new BehaviorSubject([])

  public PropertyKind = PropertyKind

  @ViewChild(CapabilitySpecSelectDirective)
  public capabilitySelectDirective: CapabilitySpecSelectDirective

  public dummyCapability: CapabilitySpec

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern
  private route = inject(ActivatedRoute)
  private workCenterTypeService = inject(WorkCenterTypeService)
  private capabilitySpecService = inject(CapabilitySpecService)
  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.workCenterType = this.route.snapshot.data['type']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.workCenterType) {
      this.workCenterType = this.initNew()
      this.wctCapabilities = []
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get workCenterType$(): Observable<WorkCenterType> {
    return this._workCenterType$.asObservable()
  }

  public get workCenterType(): WorkCenterType {
    return this._workCenterType$.getValue()
  }

  @Input()
  public set workCenterType(wct: WorkCenterType) {
    if (wct) {
      this._creationMode = !wct.id

      this._workCenterType$.next(wct)
      this.updateCapacities(wct.capabilitySpecIds)
    }
  }

  public get wctCapabilities$(): Observable<CapabilitySpec[]> {
    return this._wctCapabilities$.asObservable()
  }

  public get wctCapabilities(): CapabilitySpec[] {
    return this._wctCapabilities$.getValue()
  }

  public set wctCapabilities(wctCapabilities: CapabilitySpec[]) {
    if (wctCapabilities) {
      this._wctCapabilities$.next(wctCapabilities)
    }
  }

  public get wctCapabilitiesIds(): string[] {
    return this.wctCapabilities.map((c) => c.id)
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  public onAddCapability(capability: CapabilitySpec): void {
    if (capability) {
      const capabilities = [...this.wctCapabilities]
      capabilities.push(capability)
      this.wctCapabilities = capabilities
      this.dummyCapability = null
      this.capabilitySelectDirective.clear()
    }
  }

  public onRemoveCapabilities(toRemove: CapabilitySpec[]) {
    const toRemoveIds = toRemove.map((rc) => rc.id)
    const capabilities = this.wctCapabilities
    this.wctCapabilities = capabilities.filter((c) => !toRemoveIds.includes(c.id))
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.workCenterType.id) {
      this.workCenterTypeService.findById(this.workCenterType.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.workCenterType.capabilitySpecIds = this.wctCapabilities.map((c) => c.id)

    this.workCenterTypeService.save(this.workCenterType).subscribe({
      next: (updated) => {
        this.workCenterType = updated
        this.updateCapacities(updated.capabilitySpecIds)
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.workCenterTypeService.delete(this.workCenterType).subscribe({
      next: (_) => {
        this.emitClose()
        this.toastService.info('messages.deleting_successful')
      },
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): WorkCenterType {
    return new WorkCenterType()
  }

  private updateCapacities(ids: string[]): void {
    if (ids && ids.length > 0) {
      this.capabilitySpecService.findAllById(ids).subscribe({
        next: (page) => (this.wctCapabilities = page.content),
        error: (err) => this.toastService.error('messages.saving_failed', err),
      })
    }
  }
}
